import React, { useState, useEffect, useMemo } from "react";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getAliasesByProduct } from "../../../common/services/products/aliasService";
import { getOrganizations } from "../../../common/services/auth/organizationService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const ProductAliases = ({ productId }) => {
  const { t } = useTranslation(["products"]);
  const [aliases, setAliases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch aliases and organizations data when component mounts or ID changes
  useEffect(() => {
    const fetchData = async () => {
      if (!productId) return;

      try {
        setIsLoading(true);
        // Fetch organizations first
        const orgs = await getOrganizations();

        // Then fetch aliases and map them using the organizations data
        const aliasData = await getAliasesByProduct(productId);

        const formattedData = mapToViewModel(aliasData, orgs);
        setAliases(formattedData);
      } catch (error) {
        console.error("Error fetching product aliases:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [productId]);

  // Map API data to view model with formatted dates
  const mapToViewModel = (aliasData, orgs) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return aliasData.map((alias) => ({
      id: alias.aliasId,
      name: alias.name,
      organization: orgs.find(
        (org) => org.organizationId === alias.organizationId
      )?.name,
      organizationId: alias.organizationId,
      active: alias.active,
      activeString: alias.active,
      updatedByUserAccountId: alias.createdByUserAccountId,
      // updatedBy:
      //   alias.updatedByUserAccountFirstName +
      //   " " +
      //   alias.updatedByUserAccountLastName,
      updatedDate: new Date(alias.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      // writeDate: new Date(alias.writeDate).toLocaleDateString(
      //   "en-US",
      //   dateFormatOptions
      // ),
      eventId: alias.eventId,
    }));
  };

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: t("Name"),
        sortable: true,
        filter: true,
        flex: 2,
      },
      {
        field: "organization",
        headerName: t("Organization"),
        sortable: true,
        filter: true,
        flex: 2,
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        filter: true,
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      // {
      //   field: "updatedBy",
      //   headerName: t("UpdatedBy"),
      //   sortable: true,
      //   filter: true,
      //   flex: 2,
      // },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
        flex: 1.5,
      },
      // {
      //   field: "writeDate",
      //   headerName: t("WriteDate"),
      //   sortable: true,
      //   filter: true,
      //   width: 180,
      // },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
        flex: 2,
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 80,
      autoHeight: true,
    }),
    []
  );

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("ProductID")} {productId}
      </div>
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={aliases}
        getRowId={(params) => params.data.id.toString()}
        animateRows={true}
        domLayout="autoHeight"
        pagination={true}
        paginationPageSize={10}
      />
    </div>
  );
};

export default ProductAliases;

